import { Check, Database, FileText, PenLine } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useListSessions } from "@/hooks/api";
import { appealTypesLabel } from "@/lib/document-kinds";
import { useAppStore } from "@/store/app";
import type { SessionSummary } from "@/api/types";

/** Short, copy-free session label — the leading chunk of the UUID. */
function shortId(id: string): string {
  return id.slice(0, 8);
}

function whenLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function SessionRow({
  s,
  active,
  onPick,
}: {
  s: SessionSummary;
  active: boolean;
  onPick: () => void;
}) {
  const appeal = appealTypesLabel(s.appeal_types);
  return (
    <DropdownMenuItem
      onSelect={onPick}
      className="flex flex-col items-start gap-0.5 py-2"
    >
      <div className="flex w-full items-center gap-2">
        {active ? (
          <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
        ) : (
          <span className="w-3.5 shrink-0" />
        )}
        <span className="font-mono text-xs font-medium">{shortId(s.id)}</span>
        <span className="ml-auto text-[11px] text-muted-foreground">
          {whenLabel(s.last_accessed)}
        </span>
      </div>
      <div className="flex w-full items-center gap-2 pl-[22px] text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <FileText className="h-3 w-3" />
          {s.document_count}
        </span>
        {s.has_letter && (
          <span className="inline-flex items-center gap-1 text-sky-600">
            <PenLine className="h-3 w-3" />
            letter
          </span>
        )}
        {appeal && <span className="truncate">{appeal}</span>}
      </div>
    </DropdownMenuItem>
  );
}

export function DebugSessionSwitcher() {
  const sessionId = useAppStore((s) => s.sessionId);
  const switchSession = useAppStore((s) => s.switchSession);
  const { data: sessions } = useListSessions(true);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-1.5">
          <Database className="w-3.5 h-3.5" />
          Sessions
          <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] tabular-nums">
            {sessions.length}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-[70vh] w-80 overflow-y-auto">
        <DropdownMenuLabel className="flex items-center gap-1.5 text-xs">
          <Database className="h-3.5 w-3.5" />
          Debug — switch session
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {sessions.length === 0 ? (
          <div className="px-2 py-3 text-center text-xs text-muted-foreground">
            No sessions on the server yet.
          </div>
        ) : (
          sessions.map((s) => (
            <SessionRow
              key={s.id}
              s={s}
              active={s.id === sessionId}
              onPick={() => {
                if (s.id !== sessionId) switchSession(s.id);
              }}
            />
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
